import {
  AdAnalyticsDailyPerformance,
  AdAnalyticsFilter,
  AdAnalyticsReport,
  AdAnalyticsVideo,
} from '@kaizenplatform/hugot-entity';
import { AdAnalyticsPlacementTypes } from '@kaizenplatform/hugot-entity';
import { History } from 'history';
import { List } from 'immutable';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import WithTotalCountArrayPageInfo from '../entity/WithTotalCountArrayPageInfo';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import AdAnalyticsDailyPerformanceWebApi from '../service/persistent/AdAnalyticsDailyPerformanceWebApi';
import AdAnalyticsPlacementTypesWebApi from '../service/persistent/AdAnalyticsPlacementTypesWebApi';
import AdAnalyticsReportWebApi from '../service/persistent/AdAnalyticsReportWebApi';
import AdAnalyticsVideoWebApi from '../service/persistent/AdAnalyticsVideoWebApi';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import {
  setAdAnalyticsPlacementTypes,
  startFetchingAdAnalyticsPlacementTypes,
} from '../thunk/adAnalyticsPlacementTypes';
import {
  disposeAdAnalyticsReport,
  finishFetchingAdAnalyticsReport,
  setAdAnalyticsReport,
  setSubmitComment,
  startFetchingAdAnalyticsReport,
} from '../thunk/adAnalyticsReport';
import {
  disposeAdAnalyticsVideos,
  finishFetchingAdAnalyticsVideos,
  setAdAnalyticsDailyPerformances,
  setAdAnalyticsVideos,
  startFetchingAdAnalyticsVideos,
} from '../thunk/adAnalyticsVideo';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';

class ShowAdAnalyticsReportUsecase {
  private readonly store: Store<State>;
  private readonly errorPresenter: ErrorPresenter;
  private readonly history: History;
  private readonly organizationWebApi: OrganizationWebApi;
  private readonly adAnalyticsReportWebApi: AdAnalyticsReportWebApi;
  private readonly adAnalyticsVideoWebApi: AdAnalyticsVideoWebApi;
  private readonly adAnalyticsDailyPerformanceWebApi: AdAnalyticsDailyPerformanceWebApi;
  private readonly adAnalyticsPlacementTypesWebApi: AdAnalyticsPlacementTypesWebApi;

  constructor({
    store,
    history,
    errorPresenter,
    organizationWebApi,
    adAnalyticsReportWebApi,
    adAnalyticsVideoWebApi,
    adAnalyticsDailyPerformanceWebApi,
    adAnalyticsPlacementTypesWebApi,
  }: {
    store: Store<State>;
    history: History;
    errorPresenter: ErrorPresenter;
    organizationWebApi: OrganizationWebApi;
    adAnalyticsReportWebApi: AdAnalyticsReportWebApi;
    adAnalyticsVideoWebApi: AdAnalyticsVideoWebApi;
    adAnalyticsDailyPerformanceWebApi: AdAnalyticsDailyPerformanceWebApi;
    adAnalyticsPlacementTypesWebApi: AdAnalyticsPlacementTypesWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.errorPresenter = errorPresenter;
    this.organizationWebApi = organizationWebApi;
    this.adAnalyticsReportWebApi = adAnalyticsReportWebApi;
    this.adAnalyticsVideoWebApi = adAnalyticsVideoWebApi;
    this.adAnalyticsDailyPerformanceWebApi = adAnalyticsDailyPerformanceWebApi;
    this.adAnalyticsPlacementTypesWebApi = adAnalyticsPlacementTypesWebApi;
  }

  public prepare = async (): Promise<void> => {
    this.store.dispatch(startFetchingAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsReport());
    this.store.dispatch(startFetchingAdAnalyticsPlacementTypes());

    const { reportID } = this.getQueryParameters();

    let reportItem: AdAnalyticsReport;
    let videos: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };
    let dailyPerformance: List<AdAnalyticsDailyPerformance>;
    let placementTypes: AdAnalyticsPlacementTypes;

    const query = parse(this.store.getState().location.search.slice(1));
    const organizationId = query.org || null;
    let organization: OrganizationDetailFragment | null = null;

    try {
      if (organizationId) {
        organization = await this.organizationWebApi.getById(organizationId);
        if (organization) this.store.dispatch(setLastAccessedOrganization(organization));
      }
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
      return;
    }

    // reportを取得
    try {
      reportItem = await this.fetchAdAnalyticsReport({ id: reportID });
      placementTypes = await this.featchAdAnalyticsPlacementTypes();
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsReport());
      this.store.dispatch(disposeAdAnalyticsReport());
      this.errorPresenter.showAsFatal(err);
      return;
    }
    this.store.dispatch(setAdAnalyticsPlacementTypes(placementTypes));
    this.store.dispatch(setAdAnalyticsReport(reportItem));
    this.store.dispatch(finishFetchingAdAnalyticsReport());
    // reportに含まれるvideoIdsを元にvideo, dailyperformanceを取得
    if (!reportItem) {
      return;
    }

    const videoIds: string[] = [];
    reportItem.adVideos.map((video) => videoIds.push(video.adVideoId));

    try {
      videos = await this.fetchAdAnalyticsVideos({ adVideoIds: videoIds });
      dailyPerformance = await this.featchAdAnalyticsDailyPerformance({ adVideoIds: videoIds });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.store.dispatch(disposeAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    this.store.dispatch(setAdAnalyticsVideos(videos.adAnalyticsVideos, videos.pageInfo));
    this.store.dispatch(setAdAnalyticsDailyPerformances(dailyPerformance));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
  };

  public fetchAdAnalyticsReport = async ({ id }: { id: string }): Promise<AdAnalyticsReport> => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    return await this.adAnalyticsReportWebApi.getAdAnalyticsReport({
      id: id,
    });
  };

  public fetchAdAnalyticsVideos = async ({
    platformKey,
    mediaKey,
    placementKey,
    period = 'lifetime',
    adVideoIds = null,
  }: AdAnalyticsFilter & {
    adVideoIds?: string[] | null;
  }): Promise<{ adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo }> => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    return await this.adAnalyticsVideoWebApi.getAllOfAdAnalyticsVideo({
      platformKey,
      mediaKey,
      placementKey,
      period,
      adVideoIds,
      filters: [],
      organizationId: organization ? organization.id : '',
    });
  };

  public featchAdAnalyticsDailyPerformance = async ({
    platformKey,
    mediaKey,
    placementKey,
    adVideoIds = null,
  }: AdAnalyticsFilter & {
    adVideoIds?: string[] | null;
  }): Promise<List<AdAnalyticsDailyPerformance>> => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    return await this.adAnalyticsDailyPerformanceWebApi.getAdAnalyticsDailyPerformance({
      platformKey,
      mediaKey,
      placementKey,
      adVideoIds,
      organizationId: organization ? organization.id : '',
    });
  };

  public featchAdAnalyticsPlacementTypes = async (): Promise<AdAnalyticsPlacementTypes> =>
    await this.adAnalyticsPlacementTypesWebApi.getAdAnalyticsPlacementTypes();

  public tryUpdateReport = async ({ comment }: { comment: string }) => {
    const { reportID } = this.getQueryParameters();
    try {
      await this.adAnalyticsReportWebApi.updateReport({
        id: reportID,
        body: comment,
      });
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    }
    this.store.dispatch(setSubmitComment(true));
  };

  public checkSubmitComment = (): void => {
    this.store.dispatch(setSubmitComment(false));
  };

  public goAnalyticsReportListPage = () => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    const orgId = organization ? organization.id : '';

    this.history.push(`/ad-creatives-report${orgId !== '' ? `?org=${orgId}` : ''}`);
  };

  public getQueryParameters = () => {
    const query = parse(this.store.getState().location.search.slice(1));
    const organizationID = query.org;
    const reportID = query.id;

    return { organizationID, reportID };
  };

  public dispose = (): void => {
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(disposeAdAnalyticsReport());
  };
}

export default ShowAdAnalyticsReportUsecase;
